import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  ListChecks,
  Maximize2,
  MessageSquare,
  Minimize2,
  X,
} from "lucide-react";
import NotesTab from "../notes/workflow/profile/view/NotesTab";
import { AvatarRing } from "../../common/comms/AvatarRing";
import {
  DockIconButton,
  FloatingPanel,
  PanelHeader,
  SegmentedControl,
} from "../../common/floatingDock";
import { ApiContext } from "../../context/MyContext";
import { useClientRoom } from "../../hooks/comms/useClientRoom";
import { useCommsIdentity } from "../../hooks/comms/useCommsIdentity";
import { useRoomsUnread } from "../../hooks/comms/RoomsUnreadProvider";
import { serializeUserNotesKey } from "../../utils/listCache/caches";
import { CHAT_DOCK_ANCHOR_KEY, CHAT_DOCK_SIZE_KEY } from "./chatDockKeys";
import { BulkComposePane, BULK_MAX_RECIPIENTS } from "./BulkComposePane";
import { BulkSelectionBar } from "./BulkSelectionBar";
import { ChatDockUserList } from "./ChatDockUserList";
import { DockChatBody } from "./DockChatBody";

/** Stable avatar colour per client — see ChatDockUserList for the rationale. */
const colorIndexFor = (id) => (id ? String(id).charCodeAt(0) % 8 : 0);

/**
 * The floating chat panel: a roster, one client's chat or notes, and the
 * multi-select send.
 *
 * Four views share one shell. `users` is the root; picking a client moves to
 * `chat`, and the header's back control returns. `select` is a mode over the
 * same roster — tick clients, then compose once for all of them. All the
 * chrome — frame, header, switch, icon actions — comes from
 * `common/floatingDock`, so this file only decides what goes in the slots.
 *
 * @param {Object} props
 * @param {'users'|'select'|'chat'|'notes'} props.view
 * @param {string|null} props.clientId
 * @param {boolean} props.expanded
 * @param {boolean} [props.isMobile=false]
 * @param {(clientId: string) => void} props.onSelectClient
 * @param {(view: 'users'|'chat'|'notes') => void} props.onViewChange
 * @param {() => void} props.onBack
 * @param {() => void} props.onToggleExpanded
 * @param {() => void} props.onClose
 */
export function ChatDockPanel({
  view,
  clientId,
  expanded,
  isMobile = false,
  onSelectClient,
  onViewChange,
  onBack,
  onToggleExpanded,
  onClose,
}) {
  const navigate = useNavigate();
  const { empId } = useContext(ApiContext);
  const { getClientName } = useCommsIdentity();
  const { totalUnread, clientRoomIndex } = useRoomsUnread();
  // In-memory lookup against rooms RoomsUnreadProvider already fetched — no
  // request of its own. null for a client with no coaching room.
  const clientRoom = useClientRoom(clientId);

  // Multi-select lives here rather than in the provider: the provider persists
  // to localStorage, and a half-built recipient list should not survive a
  // reload when the next press sends to all of it.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [composing, setComposing] = useState(false);

  const isSelect = view === "select";
  const isRoster = (view === "users" || !clientId) && !isSelect;
  const isChat = view === "chat";
  const clientName =
    clientRoom?.clientName ||
    (clientId ? getClientName(clientId) : undefined) ||
    "Client";

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  /**
   * Select-all over the ids the roster is currently SHOWING.
   *
   * Unticking removes only those ids — a selection made before a search
   * narrowed the list survives, which is what the running count in the footer
   * already implies.
   */
  const toggleAllVisible = useCallback((visibleIds, allSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const enterSelect = useCallback(() => {
    setSelectedIds(new Set());
    setComposing(false);
    onViewChange("select");
  }, [onViewChange]);

  /**
   * Enter multi-select with a set already chosen — the roster's nudge card
   * hands over exactly the clients the active filter matched, so the coach does
   * not re-pick by hand the list they just filtered to.
   * @param {string[]} clientIds
   */
  const enterSelectWith = useCallback(
    (clientIds) => {
      setSelectedIds(new Set(clientIds));
      setComposing(false);
      onViewChange("select");
    },
    [onViewChange]
  );

  const exitSelect = useCallback(() => {
    setSelectedIds(new Set());
    setComposing(false);
    onViewChange("users");
  }, [onViewChange]);

  /**
   * The selection resolved to something sendable. A client whose room vanished
   * between ticking and sending is dropped rather than sent with a null room —
   * `clientRoomIndex` is refreshed on a timer under us.
   */
  const recipients = useMemo(() => {
    const out = [];
    for (const id of selectedIds) {
      const room = clientRoomIndex.get(String(id));
      if (!room) continue;
      out.push({
        clientId: String(id),
        roomId: room.roomId,
        name:
          room.clientName || getClientName(id) || `Client #${room.displayId ?? id}`,
      });
    }
    return out;
  }, [selectedIds, clientRoomIndex, getClientName]);

  /**
   * After a send: keep only the recipients that failed, so Retry is one press.
   *
   * DOES NOT LEAVE THE COMPOSE PANE. It used to `setComposing(false)` on a
   * clean batch, which unmounted the pane the instant the send finished — before
   * the coach could press Done, which is the only thing that drops the batch
   * from the store. The finished batch then outlived its own view, and the next
   * compose opened straight back into it and bounced the coach out again: the
   * "it won't let me send another one" bug. Leaving is now always an explicit
   * act (`onDone` / `onBack`), and those do clear the batch.
   *
   * A clean batch also leaves the selection alone, so `Send another` still has
   * the people it is about to write to. `onDone` clears it.
   */
  const handleSent = useCallback((failedClientIds) => {
    if (failedClientIds.length === 0) return;
    setSelectedIds(new Set(failedClientIds.map(String)));
  }, []);

  /** The coach is finished with this send: leave the pane and drop the list. */
  const handleDone = useCallback(() => {
    setSelectedIds(new Set());
    setComposing(false);
  }, []);

  /** The user-details route — the same target the split view's expand uses. */
  const openUserDetails = useCallback(() => {
    if (clientId) navigate(`/dashboard/${clientId}`);
  }, [navigate, clientId]);

  const openInMessages = useCallback(() => {
    if (clientRoom) navigate(`/messages/${clientRoom.roomId}`);
  }, [navigate, clientRoom]);

  const actions = (
    <>
      {isRoster && (
        <DockIconButton
          Icon={ListChecks}
          label="Message several clients"
          onClick={enterSelect}
        />
      )}
      {!isRoster && isChat && clientRoom && (
        <DockIconButton
          Icon={ExternalLink}
          label="Open in Messages"
          onClick={openInMessages}
        />
      )}
      {/* Now meaningful on mobile too: the panel is a bottom sheet there, so
          expanding lifts it rather than doing nothing.

          The design's expanded mobile artboard shows only a close button,
          implying drag-to-collapse. We keep the toggle instead — without the
          gesture, dropping it would strand the sheet at full height with no way
          back short of closing it. */}
      <DockIconButton
        Icon={expanded ? Minimize2 : Maximize2}
        label={expanded ? "Collapse panel" : "Expand panel"}
        onClick={onToggleExpanded}
      />
      <DockIconButton Icon={X} label="Close chat" onClick={onClose} />
    </>
  );

  // Escape unwinds one level rather than always closing: from a conversation it
  // returns to the roster, and only from the roster does it dismiss the dock.
  // Select mode adds a level — compose backs out to the selection, and the
  // selection backs out to the plain roster — so Escape never discards a
  // recipient list in one keystroke.
  const handleEscape = useCallback(() => {
    if (isSelect && composing) setComposing(false);
    else if (isSelect) exitSelect();
    else if (isRoster) onClose();
    else onBack();
  }, [isSelect, composing, exitSelect, isRoster, onClose, onBack]);

  return (
    <FloatingPanel
      label="Chat"
      expanded={expanded}
      isMobile={isMobile}
      storageKey={CHAT_DOCK_SIZE_KEY}
      anchorKey={CHAT_DOCK_ANCHOR_KEY}
      onEscape={handleEscape}
    >
      <PanelHeader
        leading={
          isRoster ? (
            <span
              aria-hidden="true"
              className="ml-0.5 flex h-8 w-8 items-center justify-center rounded-xl bg-light-orange text-orange-primary"
            >
              <MessageSquare className="h-4 w-4" />
            </span>
          ) : (
            <DockIconButton
              Icon={ChevronLeft}
              label={isSelect ? "Cancel selection" : "Back to clients"}
              onClick={isSelect ? exitSelect : onBack}
            />
          )
        }
        actions={actions}
        below={
          isRoster || isSelect ? null : (
            <SegmentedControl
              label="Chat or notes"
              value={isChat ? "chat" : "notes"}
              onChange={onViewChange}
              items={[
                {
                  id: "chat",
                  label: "Chat",
                  Icon: MessageSquare,
                  count: clientRoom?.unread || 0,
                },
                { id: "notes", label: "Notes", Icon: FileText },
              ]}
            />
          )
        }
      >
        {isSelect ? (
          <span className="block">
            <span className="block text-sm font-semibold leading-tight text-gray-900">
              {composing ? "Write message" : "Pick clients"}
            </span>
            <span className="block text-xs leading-tight text-gray-400">
              {composing
                ? `Going to ${recipients.length}`
                : "Tick everyone who should get it"}
            </span>
          </span>
        ) : isRoster ? (
          <span className="block">
            <span className="block text-sm font-semibold leading-tight text-gray-900">
              Messages
            </span>
            <span className="block text-xs leading-tight text-gray-400">
              {totalUnread > 0
                ? `${totalUnread} unread`
                : "You're all caught up"}
            </span>
          </span>
        ) : (
          /* The identity is the route-out affordance: picking a row opens the
             chat, clicking the name here leaves for the full profile.

             `inline-flex max-w-full` rather than `w-full`: the button must take
             only the width of its own content, so the rest of the title area
             stays empty header — which is what the panel's move gesture grabs.
             A full-width button here would leave nowhere to drag from. */
          <button
            type="button"
            onClick={openUserDetails}
            title={`Open ${clientName}'s details`}
            className="group -mx-1 inline-flex max-w-full items-center gap-2 rounded-lg px-1 py-0.5 text-left align-middle transition-colors hover:bg-gray-50"
          >
            <AvatarRing
              name={clientName}
              size="sm"
              colorIndex={colorIndexFor(clientId)}
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-0.5">
                <span className="min-w-0 truncate text-sm font-semibold leading-tight text-gray-900">
                  {clientName}
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-orange-primary motion-reduce:transition-none"
                />
              </span>
              <span className="block truncate text-xs leading-tight text-gray-400 group-hover:text-orange-primary">
                View profile
              </span>
            </span>
          </button>
        )}
      </PanelHeader>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-app-bg">
        {isSelect ? (
          composing ? (
            <BulkComposePane
              recipients={recipients}
              onBack={() => setComposing(false)}
              onDone={handleDone}
              onSent={handleSent}
            />
          ) : (
            <>
              <ChatDockUserList
                onSelect={onSelectClient}
                selectable
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleAll={toggleAllVisible}
              />
              <BulkSelectionBar
                selectedCount={recipients.length}
                maxRecipients={BULK_MAX_RECIPIENTS}
                onClear={() => setSelectedIds(new Set())}
                onCompose={() => setComposing(true)}
              />
            </>
          )
        ) : isRoster ? (
          <ChatDockUserList
            onSelect={onSelectClient}
            expanded={expanded}
            onNudgeMatches={enterSelectWith}
          />
        ) : isChat ? (
          // Mounted only on the chat view, never hidden — see the mount
          // discipline note in DockChatBody.
          <DockChatBody
            roomId={clientRoom?.roomId ?? null}
            viewerUserId={empId}
          />
        ) : (
          <div className="h-full overflow-y-auto p-3">
            <NotesTab
              key={clientId}
              profileId={clientId}
              cacheKey={serializeUserNotesKey(clientId)}
            />
          </div>
        )}
      </div>
    </FloatingPanel>
  );
}

export default ChatDockPanel;
